import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import Layout from "@/components/layout/Layout";
import { useSubscription } from "@/hooks/use-subscription";
import {
  marketingBulletsFromPlanFeatures,
  normalizeOwnerPlanFeaturesJson,
} from "@/lib/owner-plan-features";
import { useStripeCheckout } from "@/hooks/use-stripe-checkout";
import { useStripePortal } from "@/hooks/use-stripe-portal";

function statusVariant(
  status: string
): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "active":
    case "trialing":
      return "default";
    case "past_due":
      return "destructive";
    case "canceled":
      return "secondary";
    default:
      return "outline";
  }
}

function invoiceStatusVariant(
  status: string
): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "paid":
      return "default";
    case "failed":
      return "destructive";
    case "void":
      return "secondary";
    default:
      return "outline";
  }
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IE", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatCurrency(amount: number, currency = "eur"): string {
  return new Intl.NumberFormat("en-IE", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amount);
}

export default function SubscriptionPage() {
  const [searchParams] = useSearchParams();
  const { plan, subscription, invoices, usage, plans, isLoading, refetch } =
    useSubscription();
  const { checkout, isLoading: isCheckoutLoading } = useStripeCheckout();
  const { openPortal, isLoading: isPortalLoading } = useStripePortal();

  // Handle Stripe redirect back with ?success=true
  useEffect(() => {
    if (searchParams.get("success") === "true") {
      toast.success("Payment successful! Your subscription is now active.");
      refetch();
    }
  }, []);

  if (isLoading) {
    return (
      <Layout>
        <div className="container mx-auto py-8">
          <div className="max-w-4xl mx-auto space-y-6">
            <Skeleton className="h-10 w-64" />
            <div className="grid gap-6 md:grid-cols-2">
              <Skeleton className="h-48" />
              <Skeleton className="h-48" />
            </div>
            <Skeleton className="h-64" />
          </div>
        </div>
      </Layout>
    );
  }

  const hasSubscription = !!subscription && subscription.status !== "canceled";

  return (
    <Layout>
      <div className="container mx-auto py-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold tracking-tight">
              Subscription Management
            </h1>
            <p className="text-muted-foreground">
              Manage your nursing home subscription plan and billing
            </p>
          </div>

          {hasSubscription ? (
            /* ── Active subscription view ── */
            <>
              <div className="grid gap-6 md:grid-cols-2">
                {/* Current Plan */}
                <Card>
                  <CardHeader>
                    <CardTitle>Current Plan</CardTitle>
                    <CardDescription>Your active subscription details</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Plan</span>
                        <Badge variant="secondary">{plan?.name ?? "—"}</Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Status</span>
                        <Badge variant={statusVariant(subscription.status)}>
                          {subscription.status.charAt(0).toUpperCase() +
                            subscription.status.slice(1)}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Monthly Cost</span>
                        <span>
                          {plan ? formatCurrency(plan.price_monthly) : "—"}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Next Billing</span>
                        <span>{formatDate(subscription.current_period_end)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Usage */}
                <Card>
                  <CardHeader>
                    <CardTitle>Usage Summary</CardTitle>
                    <CardDescription>Current period usage overview</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Nursing Homes</span>
                        <span>
                          {usage
                            ? `${usage.nursingHomes.used} / ${usage.nursingHomes.max}`
                            : "—"}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Media Uploads</span>
                        <span>
                          {usage
                            ? `${usage.uploads.used} / ${usage.uploads.max}`
                            : "—"}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Billing History */}
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle>Billing History</CardTitle>
                  <CardDescription>Your recent subscription payments</CardDescription>
                </CardHeader>
                <CardContent>
                  {invoices.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No invoices yet.</p>
                  ) : (
                    <div className="space-y-3">
                      {invoices.map((invoice) => (
                        <div
                          key={invoice.id}
                          className="flex items-center justify-between py-2 border-b last:border-0"
                        >
                          <div>
                            <p className="font-medium">
                              {formatDate(invoice.invoice_date)}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {plan?.name ?? "Subscription"}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium">
                              {formatCurrency(invoice.amount, invoice.currency)}
                            </p>
                            <Badge
                              variant={invoiceStatusVariant(invoice.status)}
                              className="text-xs"
                            >
                              {invoice.status.charAt(0).toUpperCase() +
                                invoice.status.slice(1)}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Actions */}
              <div className="mt-6">
                <Button onClick={openPortal} disabled={isPortalLoading}>
                  {isPortalLoading ? "Opening..." : "Manage Billing"}
                </Button>
                <p className="mt-2 text-xs text-muted-foreground">
                  Change plan, update payment method, or cancel via Stripe's secure
                  portal.
                </p>
              </div>
            </>
          ) : (
            /* ── No subscription — show plan picker ── */
            <>
              <p className="mb-6 text-muted-foreground">
                Choose a plan to get started and unlock all features for your nursing
                home.
              </p>

              {plans.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No plans available at the moment. Please check back soon.
                </p>
              ) : (
                <div className="grid gap-6 md:grid-cols-3">
                  {plans.map((p) => {
                    const features = (p.features ?? {}) as Record<string, unknown>;
                    const normalized = normalizeOwnerPlanFeaturesJson(features);
                    const featureList = marketingBulletsFromPlanFeatures(normalized);

                    return (
                      <Card key={p.id} className="flex flex-col">
                        <CardHeader>
                          <CardTitle>{p.name}</CardTitle>
                          <CardDescription>{p.description}</CardDescription>
                        </CardHeader>
                        <CardContent className="flex flex-col flex-1">
                          <div className="mb-4">
                            <span className="text-3xl font-bold">
                              {formatCurrency(p.price_monthly)}
                            </span>
                            <span className="text-muted-foreground"> / mo</span>
                          </div>

                          <ul className="space-y-1 text-sm mb-6 flex-1">
                            {featureList.map((feature) => (
                              <li key={feature} className="flex items-center gap-2">
                                <span className="text-green-600">✓</span>
                                {feature}
                              </li>
                            ))}
                            <li className="flex items-center gap-2">
                              <span className="text-green-600">✓</span>
                              Up to {normalized.max_nursing_homes} nursing{" "}
                              {normalized.max_nursing_homes === 1 ? "home" : "homes"}
                            </li>
                          </ul>

                          <Button
                            className="w-full"
                            onClick={() => checkout(p.id)}
                            disabled={isCheckoutLoading}
                          >
                            {isCheckoutLoading ? "Loading..." : "Get Started"}
                          </Button>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
